import { useCallback, useEffect, useState } from "react";
import { api, mesajEroare } from "./api.js";
import { um } from "./um.js";
import { tr } from "./i18n.js";

const MAGAZIE = ["ADMIN_TENANT", "MANAGER_TEHNIC", "MAGAZIONER"];
const CONSUM = [...MAGAZIE, "TEHNICIAN", "COORDONATOR"];
/** Cine are voie să vadă valori — D14. Oglindește filtrarea din serviciu. */
const CU_BANI = ["ADMIN_TENANT", "MANAGER_TEHNIC", "FINANCIAR", "MAGAZIONER", "AUDITOR"];

/** Valorile sunt cele din `schema.prisma`, nu o listă plauzibilă. */
const TIPURI_GESTIUNE = [
  { cod: "Magazie", titlu: "Magazie" },
  { cod: "Masina", titlu: "Mașina unui tehnician" },
  { cod: "LocatieClient", titlu: "Stoc la client" },
];

const ETICHETE_MISCARE = {
  Receptie: "recepție", Transfer: "transfer", Consum: "consum",
  Retur: "retur", Inventar: "inventar", Casare: "casare",
};

const cant = (v) => Number(v).toLocaleString("ro-RO", { maximumFractionDigits: 4 });
const lei = (v) => (v == null ? "—" :
  `${Number(v).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei`);
const cand = (v) => (v ? new Date(v).toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" }) : "—");

export default function Stoc({ eu }) {
  const [gestiuni, setGestiuni] = useState([]);
  const [gestiuneId, setGestiuneId] = useState("");
  const [solduri, setSolduri] = useState([]);
  const [miscari, setMiscari] = useState([]);
  const [deReaprovizionat, setDeReaprovizionat] = useState([]);
  const [doarSubPrag, setDoarSubPrag] = useState(false);
  const [subTab, setSubTab] = useState("solduri");
  const [formGestiune, setFormGestiune] = useState(false);
  const [formMiscare, setFormMiscare] = useState(null);
  const [inventar, setInventar] = useState(null);
  const [inventarDeschis, setInventarDeschis] = useState(null);
  const [incarca, setIncarca] = useState(false);
  const [eroare, setEroare] = useState("");
  const [eroareForm, setEroareForm] = useState("");
  const [mesaj, setMesaj] = useState("");

  const poateMagazie = MAGAZIE.includes(eu.rol);
  const poateConsuma = CONSUM.includes(eu.rol);
  const vedeBani = CU_BANI.includes(eu.rol);

  useEffect(() => {
    api.gestiuni()
      .then((l) => { setGestiuni(l); if (l.length) setGestiuneId((g) => g || l[0].id); })
      .catch((e) => setEroare(mesajEroare(e)));
    api.deReaprovizionat().then(setDeReaprovizionat).catch(() => {});
  }, []);

  const reincarca = useCallback(async () => {
    if (!gestiuneId) { setSolduri([]); setMiscari([]); return; }
    setIncarca(true);
    try {
      const [s, m, inv] = await Promise.all([
        api.solduri(gestiuneId, doarSubPrag),
        api.miscari(gestiuneId, 50),
        api.inventare(gestiuneId).catch(() => []),
      ]);
      setSolduri(s); setMiscari(m);
      setInventarDeschis(inv.find((i) => i.esteDeschis) ?? null);
    } catch (e) { setEroare(mesajEroare(e)); }
    finally { setIncarca(false); }
  }, [gestiuneId, doarSubPrag]);

  useEffect(() => { reincarca(); }, [reincarca]);

  /** `inModal` trimite refuzul acolo unde se uită omul, nu în spatele lui. */
  const fa = useCallback(async (actiune, { inModal = false } = {}) => {
    setEroare(""); setEroareForm(""); setMesaj("");
    try {
      const r = await actiune();
      await reincarca();
      api.deReaprovizionat().then(setDeReaprovizionat).catch(() => {});
      return r;
    } catch (e) {
      (inModal ? setEroareForm : setEroare)(mesajEroare(e));
      return null;
    }
  }, [reincarca]);

  const gestiuneaAleasa = gestiuni.find((g) => g.id === gestiuneId);

  return (
    <div className="continut">
      <h1>{tr("Stoc")}</h1>
      <p className="subtitlu">
        {tr("De unde pleacă piesa: din magazie, din mașina unui tehnician sau din stocul ținut la client. Costul intern al unei lucrări e cel real, din gestiunea de proveniență — nu prețul din catalog.")}
      </p>

      {eroare && <div className="eroare">{eroare}</div>}
      {mesaj && <div className="panou panou-atentie"><div className="panou-corp">{mesaj}</div></div>}

      {deReaprovizionat.length > 0 && (
        <div className="panou panou-atentie">
          <div className="panou-corp">
            <strong>{deReaprovizionat.length}</strong>{" "}
            {deReaprovizionat.length === 1 ? tr("articol e") : tr("articole sunt")} {tr("sub pragul minim:")}{" "}
            {deReaprovizionat.slice(0, 4).map((l) => `${l.articol.denumire} (${l.gestiune.denumire})`).join(", ")}
            {deReaprovizionat.length > 4 ? tr(" și încă {0}", deReaprovizionat.length - 4) : ""}.
          </div>
        </div>
      )}

      {gestiuni.length === 0 ? (
        <div className="panou">
          <div className="panou-corp gol">
            {tr("Nicio gestiune încă.")}{" "}
            {poateMagazie
              ? tr("Creează prima — o magazie centrală e locul obișnuit de pornire.")
              : tr("Cere-i magazionerului să creeze una.")}
            {poateMagazie && (
              <div className="linie-actiune" style={{ justifyContent: "center", marginTop: "1rem" }}>
                <button className="buton buton-mic buton-actiune" onClick={() => setFormGestiune(true)}>
                  {tr("gestiune nouă")}
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="taburi-sisteme">
            {gestiuni.map((g) => (
              <button
                key={g.id}
                className={"tab-sistem" + (g.id === gestiuneId ? " activ" : "")}
                onClick={() => setGestiuneId(g.id)}
              >
                {tr(g.denumire)}
                <span className="tab-numar">{g.numarArticole}</span>
              </button>
            ))}
            {poateMagazie && (
              <button className="tab-sistem" onClick={() => setFormGestiune(true)}>{tr("+ gestiune")}</button>
            )}
          </div>

          {gestiuneaAleasa && (
            <p className="nota-cautare" style={{ marginBottom: "1rem" }}>
              {tr(TIPURI_GESTIUNE.find((t) => t.cod === gestiuneaAleasa.tip)?.titlu) ?? gestiuneaAleasa.tip}
              {gestiuneaAleasa.tehnician ? ` · ${gestiuneaAleasa.tehnician.numeComplet}` : ""}
              {gestiuneaAleasa.locatie
                ? ` · ${gestiuneaAleasa.locatie.denumire} (${gestiuneaAleasa.locatie.client?.denumire ?? ""})`
                : ""}
            </p>
          )}

          <div className="linie-actiune" style={{ marginBottom: "1rem" }}>
            <button
              className={"buton buton-mic " + (subTab === "solduri" ? "buton-actiune" : "buton-sters")}
              onClick={() => setSubTab("solduri")}
            >
              {tr("solduri")}
            </button>
            <button
              className={"buton buton-mic " + (subTab === "miscari" ? "buton-actiune" : "buton-sters")}
              onClick={() => setSubTab("miscari")}
            >
              {tr("mișcări")}
            </button>
            {poateMagazie && (
              <>
                <button className="buton buton-mic buton-sters" onClick={() => setFormMiscare("receptie")}>
                  {tr("recepție")}
                </button>
                <button className="buton buton-mic buton-sters" onClick={() => setFormMiscare("transfer")}>
                  {tr("transfer")}
                </button>
                <button className="buton buton-mic buton-sters" onClick={() => setFormMiscare("casare")}>
                  {tr("casare")}
                </button>
                <button
                  className={"buton buton-mic " + (inventarDeschis ? "buton-actiune" : "buton-sters")}
                  onClick={async () => {
                    if (inventarDeschis) {
                      setInventar(await api.diferenteInventar(inventarDeschis.id).catch((e) => {
                        setEroare(mesajEroare(e)); return null;
                      }));
                    } else setFormMiscare("inventar");
                  }}
                >
                  {inventarDeschis ? tr("continuă inventarul") : tr("inventar")}
                </button>
              </>
            )}
            {poateConsuma && !poateMagazie && (
              <button className="buton buton-mic buton-sters" onClick={() => setFormMiscare("consum")}>
                {tr("consum")}
              </button>
            )}
          </div>

          {subTab === "solduri" ? (
            <div className="panou">
              <div className="panou-antet">
                <span>{tr("Solduri")} <span className="numar">{solduri.length}</span></span>
                <label className="comutator-dezactivate">
                  <input
                    type="checkbox" checked={doarSubPrag}
                    onChange={(e) => setDoarSubPrag(e.target.checked)}
                  />
                  {tr("doar ce e sub prag")}
                </label>
              </div>
              <div className="panou-corp">
                {incarca ? (
                  <div className="gol">{tr("Se încarcă…")}</div>
                ) : solduri.length === 0 ? (
                  <div className="gol">
                    {doarSubPrag ? tr("Nimic sub pragul minim în această gestiune.") : tr("Gestiune goală.")}
                  </div>
                ) : (
                  <table className="tabel">
                    <thead>
                      <tr>
                        <th>{tr("Articol")}</th><th>{tr("Cantitate")}</th><th>{tr("Prag minim")}</th>
                        {vedeBani && <><th>{tr("Cost mediu")}</th><th>{tr("Valoare")}</th></>}
                      </tr>
                    </thead>
                    <tbody>
                      {solduri.map((l) => (
                        <tr key={l.articol.id} className={l.subPrag ? "linie-estimata" : undefined}>
                          <td>
                            {tr(l.articol.denumire)}
                            <span className="cod-mic" style={{ marginLeft: ".4rem" }}>{l.articol.cod}</span>
                          </td>
                          <td>
                            {cant(l.cantitate)} {um(l.articol.unitateMasura)}
                            {l.subPrag && <span className="marcaj">{tr("sub prag")}</span>}
                          </td>
                          <td>{l.pragMinim == null ? <span className="fara-pret">—</span> : cant(l.pragMinim)}</td>
                          {vedeBani && (
                            <>
                              <td>{l.costMediuPonderat !== undefined ? lei(l.costMediuPonderat) : <span className="fara-pret">—</span>}</td>
                              <td>{l.valoare !== undefined ? lei(l.valoare) : <span className="fara-pret">—</span>}</td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          ) : (
            <div className="panou">
              <div className="panou-antet"><span>{tr("Ultimele mișcări")}</span></div>
              <div className="panou-corp">
                <p className="nota-cautare" style={{ marginTop: 0, marginBottom: ".8rem" }}>
                  {tr("Nimic nu se șterge și nimic nu se corectează în loc: o mișcare greșită se îndreaptă cu alta, nu prin rescrierea ei. Jurnalul e append-only, garantat de bază.")}
                </p>
                {miscari.length === 0 ? (
                  <div className="gol">{tr("Nicio mișcare în această gestiune.")}</div>
                ) : (
                  <table className="tabel">
                    <thead>
                      <tr>
                        <th>{tr("Când")}</th><th>{tr("Ce")}</th><th>{tr("Articol")}</th><th>{tr("Cantitate")}</th><th>{tr("Sold după")}</th>
                        {vedeBani && <th>{tr("Cost unitar")}</th>}
                        <th>{tr("Observație")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {miscari.map((m) => (
                        <tr key={m.id}>
                          <td>{cand(m.data)}</td>
                          <td><span className="pastila pastila-soft">{tr(ETICHETE_MISCARE[m.tip] ?? m.tip)}</span></td>
                          <td>{tr(m.articol?.denumire)}</td>
                          <td>{cant(m.cantitate)} {um(m.articol?.unitateMasura)}</td>
                          <td>{cant(m.soldDupa)}</td>
                          {vedeBani && <td>{m.costUnitar !== undefined ? lei(m.costUnitar) : <span className="fara-pret">—</span>}</td>}
                          <td>
                            {m.observatie || m.document || (m.fisaId ? tr("consum pe fișă") : "")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {formGestiune && (
        <FormGestiune
          eroare={eroareForm}
          laRenuntare={() => { setFormGestiune(false); setEroareForm(""); }}
          laSalvare={async (dto) => {
            const r = await fa(() => api.creeazaGestiune(dto), { inModal: true });
            if (r) {
              setGestiuni(await api.gestiuni());
              setGestiuneId(r.id);
              setFormGestiune(false);
            }
          }}
        />
      )}

      {formMiscare && formMiscare !== "inventar" && (
        <FormMiscare
          fel={formMiscare}
          gestiuneId={gestiuneId}
          gestiuni={gestiuni}
          eroare={eroareForm}
          laRenuntare={() => { setFormMiscare(null); setEroareForm(""); }}
          laSalvare={async (dto) => {
            const apel = {
              receptie: () => api.receptie(gestiuneId, dto),
              transfer: () => api.transfer(gestiuneId, dto),
              consum: () => api.consumStoc(gestiuneId, dto),
              casare: () => api.casare(gestiuneId, dto),
            }[formMiscare];
            const r = await fa(apel, { inModal: true });
            if (r) { setFormMiscare(null); setMesaj(tr("Mișcarea a fost înregistrată.")); }
          }}
        />
      )}

      {formMiscare === "inventar" && (
        <FormInventar
          eroare={eroareForm}
          laRenuntare={() => { setFormMiscare(null); setEroareForm(""); }}
          laDeschidere={async (denumire) => {
            const r = await fa(() => api.deschideInventar(gestiuneId, { denumire }), { inModal: true });
            if (r) {
              setFormMiscare(null);
              setInventar(await api.diferenteInventar(r.id).catch(() => null));
            }
          }}
        />
      )}

      {inventar && (
        <Inventar
          date={inventar}
          poateValida={poateMagazie}
          laInchidere={() => setInventar(null)}
          laNumarare={async (linii) => {
            await api.numaraInventar(inventar.inventar.id, linii).catch((e) => setEroare(mesajEroare(e)));
            setInventar(await api.diferenteInventar(inventar.inventar.id));
          }}
          laValidare={async (observatie) => {
            await fa(() => api.valideazaInventar(inventar.inventar.id, observatie));
            setInventar(null); setInventarDeschis(null);
            setMesaj(tr("Inventarul a fost validat. Soldul s-a corectat prin mișcări, nu prin rescriere."));
          }}
        />
      )}
    </div>
  );
}

// ── Gestiune nouă ───────────────────────────────────────────────────────────

function FormGestiune({ eroare, laRenuntare, laSalvare }) {
  const [d, setD] = useState({ cod: "", denumire: "", tip: "Magazie", tehnicianId: "", locatieId: "" });
  const [tehnicieni, setTehnicieni] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [clientId, setClientId] = useState("");
  const [locatii, setLocatii] = useState([]);

  useEffect(() => {
    api.tehnicieni().then(setTehnicieni).catch(() => {});
    api.clienti().then(setClienti).catch(() => {});
  }, []);

  useEffect(() => {
    if (!clientId) { setLocatii([]); return; }
    api.locatii(clientId).then(setLocatii).catch(() => {});
  }, [clientId]);

  const lipsesc = [
    d.cod.trim().length < 2 && tr("un cod de cel puțin 2 caractere"),
    d.denumire.trim().length < 2 && tr("o denumire de cel puțin 2 caractere"),
    d.tip === "Masina" && !d.tehnicianId && tr("tehnicianul mașinii"),
    d.tip === "LocatieClient" && !d.locatieId && tr("locația clientului"),
  ].filter(Boolean);
  const gata = lipsesc.length === 0;

  return (
    <div className="modal-fundal" onClick={laRenuntare}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{tr("Gestiune nouă")}</h2>
        {eroare && <div className="eroare">{eroare}</div>}
        <p className="subtitlu">
          {tr("Tipul decide ce se mai cere: mașina are un tehnician, stocul la client are o locație, magazia n-are niciuna. Regula e impusă și în bază.")}
        </p>
        <div className="formular-linie" style={{ gridTemplateColumns: "1fr 2fr" }}>
          <div className="camp">
            <label htmlFor="g-cod">{tr("Cod")}</label>
            <input id="g-cod" value={d.cod} placeholder={tr("ex. MAG-CENTRAL")}
              onChange={(e) => setD({ ...d, cod: e.target.value })} />
          </div>
          <div className="camp">
            <label htmlFor="g-denumire">{tr("Denumire")}</label>
            <input id="g-denumire" value={d.denumire} placeholder={tr("ex. Magazia centrală")}
              onChange={(e) => setD({ ...d, denumire: e.target.value })} />
          </div>
        </div>
        <div className="camp">
          <label htmlFor="g-tip">{tr("Tip")}</label>
          <select
            id="g-tip" value={d.tip}
            onChange={(e) => setD({ ...d, tip: e.target.value, tehnicianId: "", locatieId: "" })}
          >
            {TIPURI_GESTIUNE.map((t) => <option key={t.cod} value={t.cod}>{tr(t.titlu)}</option>)}
          </select>
        </div>

        {d.tip === "Masina" && (
          <div className="camp">
            <label htmlFor="g-tehnician">{tr("Al cui e autoturismul")}</label>
            <select id="g-tehnician" value={d.tehnicianId} onChange={(e) => setD({ ...d, tehnicianId: e.target.value })}>
              <option value="">{tr("alege…")}</option>
              {tehnicieni.map((t) => <option key={t.id} value={t.id}>{t.numeComplet}</option>)}
            </select>
          </div>
        )}

        {d.tip === "LocatieClient" && (
          <>
            <div className="camp">
              <label htmlFor="g-client">{tr("Client")}</label>
              <select id="g-client" value={clientId} onChange={(e) => { setClientId(e.target.value); setD({ ...d, locatieId: "" }); }}>
                <option value="">{tr("alege…")}</option>
                {clienti.map((c) => <option key={c.id} value={c.id}>{tr(c.denumire)}</option>)}
              </select>
            </div>
            <div className="camp">
              <label htmlFor="g-locatie">{tr("Locație")}</label>
              <select
                id="g-locatie" value={d.locatieId} disabled={!clientId || locatii.length === 0}
                onChange={(e) => setD({ ...d, locatieId: e.target.value })}
              >
                <option value="">{clientId ? "alege…" : tr("alege întâi clientul")}</option>
                {locatii.map((l) => <option key={l.id} value={l.id}>{tr(l.denumire)}</option>)}
              </select>
              {clientId && locatii.length === 0 && (
                <span className="nota-cautare">{tr("Clientul n-are nicio locație activă.")}</span>
              )}
            </div>
          </>
        )}

        {lipsesc.length > 0 && (
          <p className="subtitlu">{tr("Completează:")} {lipsesc.join(", ")}.</p>
        )}

        <div className="modal-actiuni">
          <button className="buton buton-mic buton-sters" onClick={laRenuntare}>{tr("renunță")}</button>
          <button
            className="buton buton-mic buton-actiune" disabled={!gata}
            onClick={() => laSalvare({
              cod: d.cod.trim(), denumire: d.denumire.trim(), tip: d.tip,
              ...(d.tip === "Masina" ? { tehnicianId: d.tehnicianId } : {}),
              ...(d.tip === "LocatieClient" ? { locatieId: d.locatieId } : {}),
            })}
          >
            {tr("creează")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Recepție / transfer / consum / casare ───────────────────────────────────

const TITLURI_MISCARE = {
  receptie: { titlu: "Recepție", nota: "O intrare schimbă costul mediu al articolului. Trece costul REAL al lotului, nu prețul din catalog." },
  transfer: { titlu: "Transfer", nota: "Piesa se mută la costul gestiunii de plecare — nu are voie să-și schimbe valoarea pe drum." },
  consum: { titlu: "Consum", nota: "O ieșire NU schimbă costul mediu: piesa pleacă la valoarea cu care stătea în raft." },
  casare: { titlu: "Casare", nota: "Motivul e obligatoriu și rămâne în jurnal — o piesă nu dispare fără explicație." },
};

function FormMiscare({ fel, gestiuneId, gestiuni, eroare, laRenuntare, laSalvare }) {
  const [articole, setArticole] = useState([]);
  const [d, setD] = useState({
    articolId: "", cantitate: "", costUnitar: "", document: "", observatie: "", catreGestiuneId: "",
  });
  const meta = TITLURI_MISCARE[fel];

  useEffect(() => { api.articoleLista().then(setArticole).catch(() => {}); }, []);

  const lipsesc = [
    !d.articolId && tr("articolul"),
    !(Number(d.cantitate) > 0) && tr("o cantitate mai mare ca zero"),
    fel === "receptie" && d.costUnitar === "" && tr("costul unitar"),
    fel === "transfer" && !d.catreGestiuneId && tr("gestiunea către care se transferă"),
    fel === "casare" && d.observatie.trim().length < 5
      && tr("motivul casării, de cel puțin 5 caractere"),
  ].filter(Boolean);
  const gata = lipsesc.length === 0;

  return (
    <div className="modal-fundal" onClick={laRenuntare}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{tr(meta.titlu)}</h2>
        <p className="subtitlu">{meta.nota}</p>
        {eroare && <div className="eroare">{eroare}</div>}

        <div className="camp">
          <label htmlFor="m-articol">{tr("Articol")}</label>
          <select id="m-articol" value={d.articolId} onChange={(e) => setD({ ...d, articolId: e.target.value })}>
            <option value="">{tr("alege…")}</option>
            {articole.map((a) => <option key={a.id} value={a.id}>{a.cod} — {tr(a.denumire)}</option>)}
          </select>
        </div>

        <div className="formular-linie" style={{ gridTemplateColumns: fel === "receptie" ? "1fr 1fr" : "1fr" }}>
          <div className="camp">
            <label htmlFor="m-cant">{tr("Cantitate")}</label>
            <input
              id="m-cant" type="number" step="0.0001" min="0.0001" value={d.cantitate}
              onChange={(e) => setD({ ...d, cantitate: e.target.value })}
            />
          </div>
          {fel === "receptie" && (
            <div className="camp">
              <label htmlFor="m-cost">{tr("Cost unitar real (lei)")}</label>
              <input
                id="m-cost" type="number" step="0.01" min="0" value={d.costUnitar}
                onChange={(e) => setD({ ...d, costUnitar: e.target.value })}
              />
            </div>
          )}
        </div>

        {fel === "transfer" && (
          <div className="camp">
            <label htmlFor="m-catre">{tr("Către gestiunea")}</label>
            <select id="m-catre" value={d.catreGestiuneId} onChange={(e) => setD({ ...d, catreGestiuneId: e.target.value })}>
              <option value="">{tr("alege…")}</option>
              {gestiuni.filter((g) => g.id !== gestiuneId).map((g) => (
                <option key={g.id} value={g.id}>{tr(g.denumire)}</option>
              ))}
            </select>
          </div>
        )}

        {fel === "receptie" && (
          <div className="camp">
            <label htmlFor="m-doc">{tr("Document (opțional)")}</label>
            <input id="m-doc" value={d.document} placeholder={tr("ex. factura 1234/2026")}
              onChange={(e) => setD({ ...d, document: e.target.value })} />
          </div>
        )}

        <div className="camp">
          <label htmlFor="m-obs">
            {fel === "casare" ? tr("Motivul casării (obligatoriu)") : tr("Observație (opțional)")}
          </label>
          <input id="m-obs" value={d.observatie} onChange={(e) => setD({ ...d, observatie: e.target.value })} />
        </div>

        {lipsesc.length > 0 && (
          <p className="subtitlu">{tr("Completează:")} {lipsesc.join(", ")}.</p>
        )}

        <div className="modal-actiuni">
          <button className="buton buton-mic buton-sters" onClick={laRenuntare}>{tr("renunță")}</button>
          <button
            className="buton buton-mic buton-actiune" disabled={!gata}
            onClick={() => laSalvare({
              articolId: d.articolId,
              cantitate: Number(d.cantitate),
              ...(fel === "receptie" ? { costUnitar: Number(d.costUnitar) } : {}),
              ...(fel === "transfer" ? { catreGestiuneId: d.catreGestiuneId } : {}),
              ...(d.document ? { document: d.document } : {}),
              ...(d.observatie ? { observatie: d.observatie } : {}),
            })}
          >
            {tr("înregistrează")}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormInventar({ eroare, laRenuntare, laDeschidere }) {
  const [denumire, setDenumire] = useState("");
  return (
    <div className="modal-fundal" onClick={laRenuntare}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{tr("Deschide un inventar")}</h2>
        {eroare && <div className="eroare">{eroare}</div>}
        <p className="subtitlu">
          {tr("Soldul scriptic se îngheață acum, la deschidere — altfel diferența s-ar schimba sub mâna celui care numără.")}
        </p>
        <div className="camp">
          <label htmlFor="i-denumire">{tr("Cum se numește")}</label>
          <input
            id="i-denumire" value={denumire} placeholder={tr("ex. Inventar anual 2026")}
            onChange={(e) => setDenumire(e.target.value)}
          />
        </div>
        <div className="modal-actiuni">
          <button className="buton buton-mic buton-sters" onClick={laRenuntare}>{tr("renunță")}</button>
          <button
            className="buton buton-mic buton-actiune" disabled={denumire.trim().length < 3}
            onClick={() => laDeschidere(denumire.trim())}
          >
            {tr("deschide")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Inventar({ date, poateValida, laInchidere, laNumarare, laValidare }) {
  const [numarate, setNumarate] = useState({});
  const [observatie, setObservatie] = useState("");

  const liniiDeTrimis = Object.entries(numarate)
    .filter(([, v]) => v !== "")
    .map(([articolId, v]) => ({ articolId, cantitateFaptica: Number(v) }));

  return (
    <div className="modal-fundal" onClick={laInchidere}>
      <div className="modal modal-lat" onClick={(e) => e.stopPropagation()}>
        <h2>{tr(date.inventar.denumire)}</h2>
        <p className="subtitlu">
          {tr("Inventarul nu editează soldul. Numeri, se compară, iar la validare se scriu mișcări care aduc soldul la ce ai numărat — așa rămâne adevărat că suma mișcărilor e egală cu soldul.")}
        </p>

        {date.nenumarate > 0 && (
          <div className="panou panou-atentie">
            <div className="panou-corp">
              {date.nenumarate} {date.nenumarate === 1 ? tr("articol nenumărat") : tr("articole nenumărate")}{tr(". Un articol nenumărat nu e „zero\" — e necunoscut, și rămâne așa până îl treci.")}
            </div>
          </div>
        )}

        <table className="tabel">
          <thead>
            <tr><th>{tr("Articol")}</th><th>{tr("Scriptic")}</th><th>{tr("Numărat")}</th><th>{tr("Diferență")}</th></tr>
          </thead>
          <tbody>
            {date.linii.map((l) => (
              <tr key={l.articolId} className={l.diferenta ? "linie-estimata" : undefined}>
                <td>
                  {tr(l.articol.denumire)}
                  <span className="cod-mic" style={{ marginLeft: ".4rem" }}>{l.articol.cod}</span>
                </td>
                <td>{cant(l.scriptic)} {um(l.articol.unitateMasura)}</td>
                <td>
                  <input
                    type="number" step="0.0001" min="0" style={{ width: "7rem" }}
                    value={numarate[l.articolId] ?? (l.faptic == null ? "" : String(l.faptic))}
                    onChange={(e) => setNumarate({ ...numarate, [l.articolId]: e.target.value })}
                  />
                </td>
                <td>
                  {l.diferenta == null
                    ? <span className="fara-pret">{tr("nenumărat")}</span>
                    : l.diferenta === 0
                      ? "0"
                      : <strong>{l.diferenta > 0 ? "+" : ""}{cant(l.diferenta)}</strong>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {poateValida && (
          <div className="camp" style={{ marginTop: "1rem" }}>
            <label htmlFor="inv-obs">{tr("Observație la validare (opțional)")}</label>
            <input id="inv-obs" value={observatie} onChange={(e) => setObservatie(e.target.value)} />
          </div>
        )}

        <div className="modal-actiuni">
          <button className="buton buton-mic buton-sters" onClick={laInchidere}>{tr("închide")}</button>
          <button
            className="buton buton-mic buton-sters" disabled={liniiDeTrimis.length === 0}
            onClick={() => { laNumarare(liniiDeTrimis); setNumarate({}); }}
          >
            {tr("salvează numărătoarea")}
          </button>
          {poateValida && (
            <button
              className="buton buton-mic buton-actiune" disabled={date.nenumarate > 0}
              onClick={() => laValidare(observatie || undefined)}
              title={date.nenumarate > 0 ? tr("Mai sunt articole nenumărate") : undefined}
            >
              {tr("validează inventarul")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
